import React, { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rarityOptions = ["Common", "Uncommon", "Rare", "Heroic", "Epic", "Legendary"];

function formatCopper(copper) {
  if (!copper || copper <= 0) return "0c";
  const gold = Math.floor(copper / 10000);
  const rem = copper % 10000;
  const silver = Math.floor(rem / 100);
  const c = rem % 100;
  const parts = [];
  if (gold) parts.push(`${gold}g`);
  if (silver) parts.push(`${silver}s`);
  if (c || !parts.length) parts.push(`${c}c`);
  return parts.join(" ");
}

function flattenIngredientsTree(ingredients) {
  const byKey = new Map();

  const addEntry = (key, label, kind, qty) => {
    if (!key) return;
    const existing = byKey.get(key);
    if (existing) {
      existing.qty += qty;
    } else {
      byKey.set(key, { key, label, kind, qty });
    }
  };

  const stack = (ingredients || []).map((ing) => ({ node: ing, mult: 1 }));

  while (stack.length) {
    const { node, mult } = stack.shift() || {};
    if (!node) continue;
    const kind = node.kind;
    const qty = typeof node.qty === "number" ? node.qty : parseInt(node.qty || 1, 10) || 1;
    const effectiveQty = qty * (mult || 1);

    if (kind === "reagent") {
      const name = node.name;
      if (!name) continue;
      addEntry(name, name, "reagent", effectiveQty);
    } else if (kind === "craft") {
      const details = node.details || {};
      const itemId = node.item_id || details.item_id || node.name;
      if (!itemId) continue;
      const label = details.item_name || itemId;
      addEntry(itemId, label, "craft", effectiveQty);
      if (details.ingredients && Array.isArray(details.ingredients)) {
        for (const child of details.ingredients) {
          stack.push({ node: child, mult: effectiveQty });
        }
      }
    }
  }

  return Array.from(byKey.values());
}

export default function BuyerPage() {
  const [items, setItems] = useState([]);
  const [notification, setNotification] = useState("");
  // cartKey -> { item_id, rarity, quantity }
  const [cart, setCart] = useState({});
  const [selectedByCategory, setSelectedByCategory] = useState({}); // category -> item_id
  const [draftSelections, setDraftSelections] = useState({}); // category -> { itemId, rarity, quantity, materials_provided? }
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [expandedMats, setExpandedMats] = useState({}); // cartKey -> boolean
  const [lineReagents, setLineReagents] = useState({}); // cartKey -> [{ key, label, kind, qty }]
  const [materialsQtyMode, setMaterialsQtyMode] = useState({}); // cartKey -> 'per-unit' | 'total'
  const [server, setServer] = useState("");
  const [character, setCharacter] = useState("");
  const [contact, setContact] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [itemsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/apothecary/items`),
          axios.get(`${API}/apothecary/admin/settings`),
        ]);
        setItems(itemsRes.data || []);
        setNotification(settingsRes.data?.customer_notification || "");
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const lines = Object.values(cart)
      .filter((v) => v.quantity > 0)
      .map((v) => ({
        item_id: v.item_id,
        rarity: v.rarity || "Common",
        quantity: v.quantity,
        materials_provided: !!v.materials_provided,
        provided_reagents: v.provided_reagents || undefined,
      }));
    if (!lines.length) {
      setQuote(null);
      return;
    }
    const fetchQuote = async () => {
      setLoadingQuote(true);
      setQuoteError(null);
      try {
        const res = await axios.post(`${API}/apothecary/quote`, { lines });
        setQuote(res.data);
        setQuoteError(null);
      } catch (e) {
        console.error(e);
        setQuote(null);
        const detail = e.response?.data?.detail;
        setQuoteError(
          typeof detail === "string"
            ? detail
            : "Unable to compute quote for one or more items. Please contact the Apothecary admin."
        );
      } finally {
        setLoadingQuote(false);
      }
    };
    fetchQuote();
  }, [cart]);

  const setCartValue = (cartKey, field, value) => {
    setCart((prev) => ({
      ...prev,
      [cartKey]: {
        ...prev[cartKey],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!quote || !quote.lines?.length) {
      setMessage("Please select at least one item.");
      return;
    }
    if (!agree) {
      setMessage("You must agree to the quoted value.");
      return;
    }
    setSubmitting(true);
    try {
      const lines = Object.values(cart)
        .filter((v) => v.quantity > 0)
        .map((v) => ({
          item_id: v.item_id,
          rarity: v.rarity || "Common",
          quantity: v.quantity,
          materials_provided: !!v.materials_provided,
          provided_reagents: v.provided_reagents || undefined,
        }));
      await axios.post(`${API}/apothecary/orders`, {
        server,
        character,
        contact,
        agree: true,
        lines,
      });
      // Reset all form state so it's clear the order was submitted
      setServer("");
      setCharacter("");
      setContact("");
      setCart({});
      setQuote(null);
      setAgree(false);
      setSelectedByCategory({});
      setDraftSelections({});
      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setMessage("Something went wrong submitting your order.");
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = items.reduce((acc, item) => {
    acc[item.category] = acc[item.category] || [];
    acc[item.category].push(item);
    return acc;
  }, {});

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center space-y-6">
        <div className="inline-flex items-center justify-center rounded-full border border-amber-500/70 bg-amber-900/40 px-4 py-1 text-xs font-semibold tracking-[0.2em] uppercase text-amber-200/80">
          Order received
        </div>
        <div className="max-w-xl space-y-3">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-wide text-amber-100">
            The Blind Apothecary has received your order and will be in contact.
          </h2>
          <p className="text-sm text-amber-200/80">
            Thank you for entrusting us with your craft. Your request has been logged and will be reviewed by the
            Apothecary. Keep an eye on your in-game whispers or Discord for follow-up.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setSubmitted(false);
          }}
          className="mt-4 inline-flex items-center justify-center rounded-md bg-gradient-to-br from-amber-700 via-amber-600 to-amber-500 hover:from-amber-600 hover:via-amber-500 hover:to-amber-400 text-black px-4 py-2 text-sm font-semibold tracking-wide shadow-[0_0_20px_rgba(245,158,11,0.45)]"
        >
          Place another order
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Customer notification banner */}
      {notification && (
        <div className="lg:col-span-3 mb-2">
          <div className="rounded-md border border-amber-600/70 bg-amber-900/40 px-3 py-2 shadow-[0_0_18px_rgba(245,158,11,0.35)]">
            <p className="text-xs text-amber-100 whitespace-pre-line">{notification}</p>
          </div>
        </div>
      )}
      <section className="lg:col-span-2 space-y-6">
        {Object.entries(grouped).map(([category, group]) => {
          const sortedGroup = [...group].sort((a, b) => a.name.localeCompare(b.name));
          const draft = draftSelections[category] || {};
          const selectedId = draft.itemId || selectedByCategory[category] || "";
          const selectedItem = sortedGroup.find((it) => it.id === selectedId) || null;
          const draftQty = draft.quantity ?? 0;
          const draftRarity = draft.rarity ?? "Common";

          return (
            <div
              key={category}
              className="rounded-xl border border-[#3c2a1c]/70 bg-[radial-gradient(circle_at_top,_#251913_0%,_#120d0b_45%,_#070606_100%)] p-4 shadow-[0_18px_40px_rgba(0,0,0,0.85)] space-y-3"
            >
              <h2 className="text-lg font-semibold text-amber-100 tracking-wide">
                {category}
              </h2>

              <div className="grid gap-3 sm:grid-cols-[2fr,auto,auto,auto] items-end">
                <div>
                  <label className="block text-xs text-amber-300 mb-1 tracking-wide">Select {category} item</label>
                  <select
                    className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 text-sm rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    value={selectedId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedByCategory((prev) => ({ ...prev, [category]: id }));
                      setDraftSelections((prev) => ({
                        ...prev,
                        [category]: {
                          itemId: id,
                          rarity: prev[category]?.rarity ?? "Common",
                          quantity: prev[category]?.quantity ?? 0,
                        },
                      }));
                    }}
                  >
                    <option value="">Choose an item…</option>
                    {[
                      "Novice",
                      "Apprentice",
                      "Journeyman",
                      "Master",
                      "Grandmaster",
                    ].map((tierLabel) => {
                      const itemsForTier = sortedGroup.filter((item) => item.tier === tierLabel);
                      if (!itemsForTier.length) return null;
                      return (
                        <optgroup key={tierLabel} label={tierLabel}>
                          {itemsForTier.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-amber-300 mb-1">Rarity</label>
                  <select
                    className="bg-[#17110e] border border-[#4b3624] text-amber-100 text-sm rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    value={draftRarity}
                    onChange={(e) => {
                      if (!selectedId) return;
                      const rarity = e.target.value;
                      setDraftSelections((prev) => ({
                        ...prev,
                        [category]: {
                          ...(prev[category] || {}),
                          itemId: selectedId,
                          rarity,
                          quantity: draftQty,
                        },
                      }));
                    }}
                    disabled={!selectedId}
                  >
                    {(selectedItem?.allowed_rarities || rarityOptions).map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-amber-300 mb-1">Amount</label>
                  <input
                    type="number"
                    min="0"
                    className="w-20 bg-[#17110e] border border-[#4b3624] text-amber-100 text-sm rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-500"
                    value={draftQty}
                    onChange={(e) => {
                      if (!selectedId) return;
                      const value = Math.max(0, Number(e.target.value || 0));
                      setDraftSelections((prev) => ({
                        ...prev,
                        [category]: {
                          ...(prev[category] || {}),
                          itemId: selectedId,
                          rarity: draftRarity,
                          quantity: value,
                        },
                      }));
                    }}
                    disabled={!selectedId}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (!selectedId) return;
                    const value = Math.max(0, Number(draftQty || 0));
                    const cartKey = `${selectedId}:${draftRarity}`;
                    setCart((prev) => ({
                      ...prev,
                      [cartKey]: {
                        item_id: selectedId,
                        rarity: draftRarity,
                        quantity: value,
                      },
                    }));
                  }}
                  className="inline-flex items-center justify-center rounded-md bg-gradient-to-br from-amber-700 via-amber-600 to-amber-500 hover:from-amber-600 hover:via-amber-500 hover:to-amber-400 text-black px-3 py-2 text-xs font-semibold tracking-wide shadow-[0_0_20px_rgba(245,158,11,0.45)] disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!selectedId}
                >
                  Add to pouch
                </button>
              </div>

              {selectedItem && (
                <p className="text-xs text-amber-200/80">
                  Selected: <span className="font-semibold">{selectedItem.name}</span> ({selectedItem.tier})
                </p>
              )}
            </div>
          );
        })}
      </section>

      <section className="space-y-4 rounded-xl border border-[#3c2a1c]/70 bg-[radial-gradient(circle_at_top,_#241612_0%,_#100b09_50%,_#050405_100%)] p-4 shadow-[0_18px_40px_rgba(0,0,0,0.85)]">
        <h2 className="text-lg font-semibold text-amber-100 tracking-wide">Order details</h2>

        {Object.keys(cart).length > 0 && (
          <div className="rounded-lg border border-[#4b3624]/80 bg-[radial-gradient(circle_at_top,_#1a120e_0%,_#070505_70%)] p-3 text-xs space-y-2 shadow-[0_14px_30px_rgba(0,0,0,0.8)]">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-amber-100">Pouch contents</span>
              <span className="text-amber-300/80">Items you have added so far</span>
            </div>
            <div className="space-y-1 pr-1">
              {Object.entries(cart)
                .filter(([, v]) => v.quantity > 0)
                .map(([cartKey, v]) => {
                  const item = items.find((it) => it.id === v.item_id);
                  const isHerbalism = item?.category === "Herbalism";
                  const isMatsToggleVisible = !!item && !isHerbalism; // customers cannot provide mats for Herbalism
                  const line = quote?.lines?.find((ln) => ln.item_id === v.item_id && ln.rarity === (v.rarity || "Common"));
                  const cartKeyRef = cartKey;
                  const currentMatsMode = v.materials_mode || "none"; // "none" | "full" | "partial"
                  if (!item) return null;
                  return (
                    <div
                      key={cartKey}
                      className="flex flex-wrap items-start gap-2 justify-between border border-amber-900/40 rounded-md px-2 py-2"
                    >
                      <div className="min-w-[140px]">
                        <div className="font-semibold text-amber-100">{item.name}</div>
                        <div className="text-amber-200/80">{item.category} – {item.tier}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {isMatsToggleVisible && (
                          <div className="flex flex-col gap-1 text-[11px] text-amber-200/80">
                            <span className="font-medium">Materials</span>
                            <div className="flex flex-col gap-0.5">
                              {/* Herbalism: only full-mats vs none */}
                              {false ? (
                                <label className="inline-flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={!!v.materials_provided}
                                    onChange={(e) => {
                                      setCartValue(cartKeyRef, "materials_provided", e.target.checked);
                                      if (e.target.checked) {
                                        setCartValue(cartKeyRef, "materials_mode", "full");
                                      } else {
                                        setCartValue(cartKeyRef, "materials_mode", "none");
                                      }
                                    }}
                                  />
                                  <span className="inline-flex items-center gap-1">
                                    <span>Customer provides all herbs</span>
                                    <span className="inline-flex items-center rounded-full border border-amber-500/60 bg-amber-900/40 px-1.5 py-0.5 text-[9px] text-amber-200/90">
                                      You provide herbs, we only charge labor.
                                    </span>
                                  </span>
                                </label>
                              ) : (
                                <>
                                  <label className="inline-flex items-center gap-1">
                                    <input
                                      type="radio"
                                      name={`mats-${cartKeyRef}`}
                                      checked={currentMatsMode === "none"}
                                      onChange={() => {
                                        setCartValue(cartKeyRef, "materials_provided", false);
                                        setCartValue(cartKeyRef, "materials_mode", "none");
                                        setCartValue(cartKeyRef, "provided_reagents", undefined);
                                        setExpandedMats((prev) => ({ ...prev, [cartKeyRef]: false }));
                                      }}
                                    />
                                    <span>Apothecary provides all materials</span>
                                  </label>
                                  <label className="inline-flex items-center gap-1">
                                    <input
                                      type="radio"
                                      name={`mats-${cartKeyRef}`}
                                      checked={currentMatsMode === "full"}
                                      onChange={() => {
                                        setCartValue(cartKeyRef, "materials_provided", true);
                                        setCartValue(cartKeyRef, "materials_mode", "full");
                                        setCartValue(cartKeyRef, "provided_reagents", undefined);
                                        setExpandedMats((prev) => ({ ...prev, [cartKeyRef]: false }));
                                      }}
                                    />
                                    <span>Customer provides all materials</span>
                                  </label>
                                  <label className="inline-flex items-center gap-1">
                                    <input
                                      type="radio"
                                      name={`mats-${cartKeyRef}`}
                                      checked={currentMatsMode === "partial"}
                                      onChange={async () => {
                                        setCartValue(cartKeyRef, "materials_provided", false);
                                        setCartValue(cartKeyRef, "materials_mode", "partial");
                                        try {
                                          if (!lineReagents[cartKeyRef]) {
                                            const res = await axios.get(
                                              `${API}/apothecary/admin/true_cost_debug/${item.id}`
                                            );
                                            const ingredients = res.data?.breakdown?.ingredients || [];
                                            const flat = flattenIngredientsTree(ingredients);
                                            setLineReagents((prev) => ({
                                              ...prev,
                                              [cartKeyRef]: flat,
                                            }));
                                          }
                                        } catch (err) {
                                          console.error("Failed to load ingredients for partial mats", err);
                                        }
                                        setExpandedMats((prev) => ({ ...prev, [cartKeyRef]: true }));
                                      }}
                                    />
                                    <span>Customer provides some materials</span>
                                  </label>
                                </>
                              )}
                            </div>
                            {/* Partial-mats ingredient checklist (reagents + crafted materials) */}
                            {!isHerbalism && currentMatsMode === "partial" && (
                              <div className="mt-1 rounded border border-amber-800/70 bg-black/30 px-2 py-1 max-w-xs">
                                <div className="flex items-center justify-between mb-1 gap-2">
                                  <p className="text-[10px] text-amber-300/90 flex flex-col gap-0.5">
                                    <span>Select which reagents or crafted materials you will provide:</span>
                                    <span className="flex items-center gap-1">
                                      <span
                                        className="underline decoration-dotted cursor-help text-amber-200/90 whitespace-nowrap"
                                        title="Quantities shown are per single unit of this crafted item. If you order multiple, multiply these amounts by your quantity."
                                      >
                                        per-unit
                                      </span>
                                      {v.quantity > 1 && (
                                        <>
                                          <span className="text-amber-400/80">|</span>
                                          <label className="inline-flex items-center gap-1">
                                            <input
                                              type="checkbox"
                                              className="h-3 w-3"
                                              checked={(materialsQtyMode[cartKeyRef] || "per-unit") === "total"}
                                              onChange={(e) => {
                                                setMaterialsQtyMode((prev) => ({
                                                  ...prev,
                                                  [cartKeyRef]: e.target.checked ? "total" : "per-unit",
                                                }));
                                              }}
                                            />
                                            <span className="whitespace-nowrap">show totals for {v.quantity}x</span>
                                          </label>
                                        </>
                                      )}
                                    </span>
                                  </p>
                                  <button
                                    type="button"
                                    className="text-[10px] text-amber-300 underline ml-2 whitespace-nowrap"
                                    onClick={() => {
                                      setExpandedMats((prev) => ({
                                        ...prev,
                                        [cartKeyRef]: !prev[cartKeyRef],
                                      }));
                                    }}
                                  >
                                    {expandedMats[cartKeyRef] ? "Hide list" : "Show list"}
                                  </button>
                                </div>
                                {/* Always keep the list visible while in partial-mats mode so customers
                                    can see what they selected. We only allow a momentary hide via the
                                    button above; changing modes or quantity will re-open it. */}
                                {expandedMats[cartKeyRef] && (
                                  <div className="space-y-0.5 max-h-32 overflow-y-auto pr-1">
                                    {(lineReagents[cartKeyRef] || []).map((ing) => {
                                      const current = v.provided_reagents || [];
                                      const checked = current.includes(ing.key);
                                      const mode = materialsQtyMode[cartKeyRef] || "per-unit";
                                      const qty = ing.qty || 0;
                                      const totalQty = mode === "total" ? qty * (v.quantity || 1) : qty;
                                      return (
                                        <label
                                          key={ing.key}
                                          className="flex items-center gap-1 text-[10px] text-amber-200"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => {
                                              const next = new Set(v.provided_reagents || []);
                                              if (e.target.checked) {
                                                next.add(ing.key);
                                              } else {
                                                next.delete(ing.key);
                                              }
                                              setCartValue(cartKeyRef, "provided_reagents", Array.from(next));
                                            }}
                                          />
                                          <span>
                                            {ing.label}
                                            {qty ? ` (x${totalQty}${mode === "total" ? " total" : ""})` : ""}
                                          </span>
                                        </label>
                                      );
                                    })}
                                    {(!lineReagents[cartKeyRef] || lineReagents[cartKeyRef].length === 0) && (
                                      <p className="text-[10px] text-amber-300/80 italic">
                                        No ingredients listed for this recipe.
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <select
                          className="bg-[#2a1d17] border border-amber-800 text-amber-100 text-xs rounded px-2 py-1"
                          value={v.rarity || "Common"}
                          onChange={(e) => setCartValue(cartKey, "rarity", e.target.value)}
                        >
                          {rarityOptions.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="0"
                          className="w-16 bg-[#2a1d17] border border-amber-800 text-amber-100 text-xs rounded px-2 py-1"
                          value={v.quantity}
                          onChange={(e) => {
                            const value = Math.max(0, Number(e.target.value || 0));
                            setCartValue(cartKey, "quantity", value);
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setCart((prev) => {
                              const next = { ...prev };
                              delete next[cartKey];
                              return next;
                            });
                          }}
                          className="text-red-300 hover:text-red-200 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="text-amber-200/80 text-right min-w-[90px]">
                        {line ? (
                          <>
                            <div>Per: {formatCopper(line.unit_price_copper)}</div>
                            <div>Total: {formatCopper(line.line_total_copper)}</div>
                          </>
                        ) : (
                          <span className="italic text-amber-300/60">Awaiting quote…</span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          <div>
            <label className="block text-amber-200 mb-1">Server / Realm (currently accepting orders on Shol only)</label>
            <input
              className="w-full bg-[#2a1d17] border border-amber-800 text-amber-100 rounded px-2 py-1"
              value={server}
              onChange={(e) => setServer(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-amber-200 mb-1">In-game Character Name</label>
            <input
              className="w-full bg-[#2a1d17] border border-amber-800 text-amber-100 rounded px-2 py-1"
              value={character}
              onChange={(e) => setCharacter(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-amber-200 mb-1">Preferred contact (Discord handle)</label>
            <input
              className="w-full bg-[#2a1d17] border border-amber-800 text-amber-100 rounded px-2 py-1"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Example: YourName#1234"
            />
          </div>

          <div className="pt-2 border-t border-amber-900/60 mt-2 text-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-amber-200">Estimated total</span>
              <span className="font-semibold text-amber-100">
                {loadingQuote ? "Calculating…" : quote ? formatCopper(quote.subtotal_copper) : "—"}
              </span>
            </div>
            {quoteError && (
              <p className="text-xs text-red-300 mb-1">
                {quoteError}
              </p>
            )}
            <p className="text-amber-300/80 text-xs">
              This value reflects The Blind Apothecary&apos;s current in-game rates for crafting time and materials. Prices
              may be adjusted over time based on the in-game economy. Payment is due in full at the time of delivery.
            </p>
          </div>

          <label className="flex items-start gap-2 text-xs text-amber-200 mt-2">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              I understand this is an in-game service and agree to the quoted value for the items I have selected.
            </span>
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="mt-3 inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-black px-4 py-2 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Submitting..." : "Submit order"}
          </button>
          {message && <p className="text-amber-200 text-xs mt-1">{message}</p>}
        </form>
      </section>
    </div>
  );
}
